import "@sphtech/dmg-design-system/dist/theme-sww.css";
import "@sphtech/dmg-design-system/assets/sww/fonts.css";

import { buildUrlWithGARefCode } from "@app/utils/ga/tracking";
import Newsticker from "@components/Modules/Newsticker/Newsticker";
import { configSite, configTrackingCode } from "@pub/config";
import { BaseLayout, CustomClassContext } from "@sphtech/dmg-design-system";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router";

import {
  configHeader,
  svgHamburgerMenu,
  svgSearchIcon,
} from "./config/configHeader";
import customStyle from "./custom.module.css";
import footerSvgSource from "./invert-logo.svg?raw"; // Importing the inverted logo for the footer
import logoSvgSource from "./logo.svg?raw";
import { RoutePaths } from "./routePaths";

type SWWBaseLayoutProps = {
  ArticleTitle?: string;
  children: React.ReactNode;
  ads?: boolean;
  leaderboard?: boolean;
  skipMobileBanner?: boolean;
};

/**
 * Reusable Base layout specific to SWW.
 */
const SWWBaseLayout = ({
  ArticleTitle,
  children,
  ads = true,
  leaderboard = true,
  skipMobileBanner,
}: SWWBaseLayoutProps) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  return (
    <CustomClassContext.Provider
      value={{
        navbar: {
          primaryItems: customStyle["primaryItemsContainer"],
        },
        homeContentBlockF: {
          anchorBlock: customStyle.homeContentBlockFAnchorBlock,
        },
        homeContentBlockD: {
          scroll: customStyle.homeContentBlockDscroll,
        },
        callBlock: {
          horizontalLine: customStyle.callBlockHorizontalLine,
        },
        homeLatestArticle: {
          item: customStyle.homeLatestArticleItem,
        },
        anchorBlock: {
          container: customStyle.anchorBlockContainer,
        },
        affiliateShopping: {
          blockContainer: customStyle.affiliateShoppingBlockContainer,
        },
        subAnchor: {
          item: customStyle.latestArticleContainer,
        },
      }}
    >
      <BaseLayout
        ArticleTitle={ArticleTitle}
        ads={ads}
        leaderboard={leaderboard}
        NewsTicker={<Newsticker />}
        RoutePaths={RoutePaths}
        svgSource={logoSvgSource}
        footerSvgSource={footerSvgSource}
        siteName={configSite.name}
        mciText={
          <span suppressHydrationWarning>
            Published by SPH Media Limited, Co. Regn. No. 202120748H. Copyright
            © {currentYear} SPH Media Limited. All rights reserved.
          </span>
        }
        socialIconProps={configHeader.socialMedia}
        megaMenuProps={{
          NewsLetterProps: {
            ...configHeader.megaMenu.newsletter,
            url:
              (configTrackingCode.signup &&
                buildUrlWithGARefCode(
                  configHeader.megaMenu.newsletter.url,
                  configTrackingCode.signup,
                )) ||
              configHeader.megaMenu.newsletter.url,
          },
          CloseButtonProps: configHeader.megaMenu.closeButton,
        }}
        currentLocation={location}
        skipMobileBanner={skipMobileBanner}
        svgHamburgerMenu={svgHamburgerMenu}
        svgSearchIcon={svgSearchIcon}
      >
        <Helmet
          htmlAttributes={{
            lang: "en-SG",
          }}
        >
          <link
            rel="icon"
            type="image/png"
            href="/favicon-96x96.png"
            sizes="96x96"
          />
          <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
          <link rel="shortcut icon" href="/favicon.ico" />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/apple-touch-icon.png"
          />
          <link rel="manifest" href="/site.webmanifest" />
        </Helmet>
        {children}
      </BaseLayout>
    </CustomClassContext.Provider>
  );
};

export default SWWBaseLayout;
